import graphql from "babel-plugin-relay/macro";
import { Provider, useSetAtom } from "jotai";
import { useCallback, useEffect, useState } from "react";
import { useFragment } from "react-relay";

import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";

import { useFlags } from "../../../../../utils/launchDarkly";
import { useOpenConfirmedBookingModalAfterCompletedBooking } from "../BookingSummary/hooks/useOpenConfirmedBookingModalAfterCompletedBooking";
import { useClearConfirmedBookingModalWhenOpen } from "../BookingSummary/hooks/useResetConfirmedBookingModalOnLeave";
import { EditBookingModal_Booking$key } from "./__generated__/EditBookingModal_Booking.graphql";
import { enterEditableRoundsPrivateStepAtom } from "./atoms/actions/EnterEditableRoundsPrivateStepAtom/enterEditableRoundsPrivateStepAtom";
import { currentModalStepAtom } from "./atoms/stores";
import { ModalStepOutlet } from "./components/ModalStepOutlet/ModalStepOutlet";
import { RestrictionsManager } from "./components/RestrictionsManager/RestrictionsManager";
import { useInitializeEditBookingModal } from "./hooks/useInitializeEditBookingModal/useInitializeEditBookingModal";
import { PatronEditBooking } from "./types";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  bookingRef: EditBookingModal_Booking$key;
  currentSessionMemberShipTypeId: string;
  currentSessionUserClientId: string;
  initialStep?: PatronEditBooking.ModalStep;
};
export const EditBookingModal = ({
  isOpen,
  onClose,
  bookingRef,
  currentSessionMemberShipTypeId,
  currentSessionUserClientId,
  initialStep,
}: Props) => {
  const { membersCanAddUnknownGuests, memberNonPlayingHostToggle } = useFlags();
  useClearConfirmedBookingModalWhenOpen(isOpen);
  const openConfirmedBookingModal =
    useOpenConfirmedBookingModalAfterCompletedBooking();
  const handleSaveSuccess = useCallback(() => {
    onClose();
    openConfirmedBookingModal();
  }, [onClose, openConfirmedBookingModal]);

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={() => {}}
      hideCloseButton={true}
      disableContentScroll={true}
      content={
        isOpen ? (
          <Provider>
            <RestrictionsManager />
            <EditBookingModalContent
              onClose={onClose}
              onSaveSuccess={handleSaveSuccess}
              bookingRef={bookingRef}
              initialStep={initialStep}
              membersCanAddUnknownGuests={membersCanAddUnknownGuests}
              memberNonPlayingHostToggle={memberNonPlayingHostToggle}
              currentSessionMemberShipTypeId={currentSessionMemberShipTypeId}
              currentSessionUserClientId={currentSessionUserClientId}
            />
          </Provider>
        ) : undefined
      }
    />
  );
};

type EditBookingModalContentProps = {
  onClose: () => void;
  onSaveSuccess: () => void;
  bookingRef: EditBookingModal_Booking$key;
  currentSessionMemberShipTypeId: string;
  currentSessionUserClientId: string;
  membersCanAddUnknownGuests: boolean;
  memberNonPlayingHostToggle: boolean;
  initialStep?: PatronEditBooking.ModalStep;
};
const EditBookingModalContent = ({
  onClose,
  onSaveSuccess,
  bookingRef,
  currentSessionMemberShipTypeId,
  currentSessionUserClientId,
  membersCanAddUnknownGuests,
  memberNonPlayingHostToggle,
  initialStep,
}: EditBookingModalContentProps) => {
  const booking = useFragment(EditBookingModalFragmentString, bookingRef);
  const [openingStep] = useState(initialStep);
  useInitializeEditBookingModal({
    bookingRef: booking,
    currentSessionMemberShipTypeId,
    currentSessionUserClientId,
  });

  const isPrivate = booking?.startSlot?.agenda?.facility?.club?.supportsPrivate;
  const setCurrentModalStep = useSetAtom(currentModalStepAtom);

  const enterEditableRoundsPrivateStep = useSetAtom(
    enterEditableRoundsPrivateStepAtom
  );
  useEffect(() => {
    if (openingStep) {
      setCurrentModalStep(openingStep);
    } else if (isPrivate) {
      enterEditableRoundsPrivateStep();
    }
  }, [
    isPrivate,
    enterEditableRoundsPrivateStep,
    openingStep,
    setCurrentModalStep,
  ]);

  return (
    <ModalStepOutlet
      currentSessionUserClientId={currentSessionUserClientId}
      bookingRef={booking}
      onClose={onClose}
      onSaveSuccess={onSaveSuccess}
      currentMembershipTypeId={currentSessionMemberShipTypeId}
      membersCanAddUnknownGuests={membersCanAddUnknownGuests}
      memberNonPlayingHostToggle={memberNonPlayingHostToggle}
    />
  );
};

const EditBookingModalFragmentString = graphql`
  fragment EditBookingModal_Booking on Booking {
    ...useInitializeEditBookingModal_Booking
    ...ModalStepOutlet_Booking
    startSlot {
      agenda {
        facility {
          club {
            supportsPrivate
          }
        }
      }
    }
  }
`;
